import React, { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { Sparkles, X, Loader2, Download } from "lucide-react";
import { Button } from "../components/ui/button";

const STATUS_STYLES = {
  new: "bg-[#F7FAFC] text-[#718096]",
  matched: "bg-[#1E88E5]/10 text-[#1E88E5]",
  accepted: "bg-[#25D366]/10 text-[#16A34A]",
  declined: "bg-[#DC2626]/10 text-[#DC2626]",
  quoted: "bg-[#FFB400]/10 text-[#FFB400]",
  approved: "bg-[#25D366]/10 text-[#16A34A]",
  started: "bg-[#1E88E5]/10 text-[#1E88E5]",
  completed: "bg-[#16A34A]/10 text-[#16A34A]",
  lost: "bg-[#DC2626]/10 text-[#DC2626]",
};

const AdminLeads = () => {
  const [items, setItems] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [openLead, setOpenLead] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [quote, setQuote] = useState(null);
  const [workerId, setWorkerId] = useState("");

  const load = () => api.get("/admin/leads").then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/workers").then((r) => setWorkers(r.data.items)).catch(() => {}); }, []);

  const openDraft = (lead) => {
    setOpenLead(lead);
    setQuote(null);
    setWorkerId(lead.matched_worker_ids?.[0] || workers[0]?.id || "");
  };

  const draftQuote = async () => {
    if (!workerId || !openLead) return;
    setDrafting(true);
    try {
      const res = await api.post("/admin/quotes/draft", { lead_id: openLead.id, worker_id: workerId });
      setQuote(res.data);
      load();
    } finally { setDrafting(false); }
  };

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold text-[#2D3748]">Leads</h1>
      <p className="text-[#718096] mt-1">All incoming work requests.</p>
      <div className="mt-6 bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7FAFC] text-[#718096] uppercase tracking-wider text-xs">
            <tr>
              <th className="text-left px-5 py-3">Customer</th>
              <th className="text-left px-5 py-3">Location</th>
              <th className="text-left px-5 py-3">Description</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-right px-5 py-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {items.map((l) => (
              <tr key={l.id} className="hover:bg-[#F7FAFC]" data-testid={`lead-row-${l.id}`}>
                <td className="px-5 py-4 font-heading font-semibold text-[#2D3748]">{l.customer_name}<div className="text-xs text-[#718096] font-normal font-sans">{l.customer_whatsapp}</div></td>
                <td className="px-5 py-4 text-[#2D3748]">{l.location}</td>
                <td className="px-5 py-4 text-[#2D3748] max-w-md">
                  <div className="line-clamp-2">{l.description}</div>
                  <div className="text-xs text-[#718096] mt-1">{l.interior_exterior || ""} {l.timeline ? `· ${l.timeline}` : ""}</div>
                  {l.photos && l.photos.length > 0 && (
                    <div className="mt-2 flex gap-1.5" data-testid={`lead-photos-${l.id}`}>
                      {l.photos.slice(0, 4).map((p, i) => (
                        <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="block">
                          <img src={p} alt="" className="w-12 h-12 rounded-md object-cover border border-[#E2E8F0] hover:border-[#25D366]" />
                        </a>
                      ))}
                      {l.photos.length > 4 && (
                        <div className="w-12 h-12 rounded-md bg-[#F7FAFC] border border-[#E2E8F0] flex items-center justify-center text-xs text-[#718096]">+{l.photos.length - 4}</div>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-5 py-4"><span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_STYLES[l.status] || ""}`}>{l.status}</span></td>
                <td className="px-5 py-4 text-right">
                  <Button onClick={() => openDraft(l)} data-testid={`draft-quote-btn-${l.id}`} size="sm" className="rounded-full bg-[#25D366] hover:bg-[#16A34A] text-white text-xs">
                    <Sparkles className="w-3 h-3 mr-1" /> Draft quote
                  </Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-[#718096]">No leads yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {openLead && (
        <div className="fixed inset-0 bg-[#2D3748]/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setOpenLead(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="draft-quote-modal">
            <div className="flex items-center justify-between p-5 border-b border-[#E2E8F0]">
              <div>
                <h2 className="font-heading font-semibold text-xl text-[#2D3748]">Draft AI quote</h2>
                <p className="text-sm text-[#718096]">{openLead.customer_name} · {openLead.location}</p>
              </div>
              <button onClick={() => setOpenLead(null)} className="p-2 hover:bg-[#F7FAFC] rounded-lg" data-testid="modal-close-btn"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-[#F7FAFC] border border-[#E2E8F0] rounded-xl p-4 text-sm">{openLead.description}</div>
              <div>
                <label className="block text-sm font-heading font-medium text-[#2D3748] mb-2">Assign painter</label>
                <select
                  data-testid="worker-select"
                  value={workerId}
                  onChange={(e) => setWorkerId(e.target.value)}
                  className="w-full rounded-xl border border-[#E2E8F0] px-3 py-2.5 bg-white"
                >
                  {workers.map((w) => <option key={w.id} value={w.id}>{w.business_name} — {w.full_name}</option>)}
                </select>
              </div>
              {!quote ? (
                <Button onClick={draftQuote} disabled={drafting || !workerId} data-testid="generate-quote-btn" className="w-full rounded-xl bg-[#25D366] hover:bg-[#16A34A] text-white py-5 font-medium">
                  {drafting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Drafting…</>) : (<><Sparkles className="w-4 h-4 mr-2" /> Generate quote with AI</>)}
                </Button>
              ) : (
                <div className="bg-white border-2 border-[#25D366]/30 rounded-2xl p-5" data-testid="quote-preview">
                  <div className="flex items-center justify-between border-b border-[#E2E8F0] pb-4 mb-4">
                    <div>
                      <div className="font-heading font-bold text-lg text-[#2D3748]">{quote.business_name}</div>
                      <div className="text-xs text-[#718096] mt-0.5">{quote.quote_number}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-[#718096]">For</div>
                      <div className="font-medium text-[#2D3748]">{quote.customer_name}</div>
                    </div>
                  </div>
                  <div className="text-sm text-[#2D3748] mb-4">{quote.work_description}</div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-[#718096] uppercase tracking-wider">
                        <th className="text-left py-2">Item</th>
                        <th className="text-right py-2">Qty</th>
                        <th className="text-right py-2">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]">
                      {quote.line_items.map((li, i) => (
                        <tr key={i}>
                          <td className="py-2 text-[#2D3748]">{li.description}</td>
                          <td className="py-2 text-right text-[#718096]">{li.quantity}</td>
                          <td className="py-2 text-right text-[#2D3748] font-medium">R {li.total.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-4 pt-4 border-t border-[#E2E8F0] flex justify-between items-center">
                    <div className="text-[#718096] text-sm">Deposit: <span className="text-[#16A34A] font-semibold">R {quote.deposit.toFixed(2)}</span></div>
                    <div className="font-heading text-2xl font-bold text-[#2D3748]">R {quote.total.toFixed(2)}</div>
                  </div>
                  {quote.payment_instructions && (
                    <div className="mt-4 p-3 bg-[#F7FAFC] border border-[#E2E8F0] rounded-lg text-xs text-[#2D3748] whitespace-pre-line">{quote.payment_instructions}</div>
                  )}
                  {quote.notes && <div className="mt-3 text-sm italic text-[#718096]">"{quote.notes}"</div>}
                  <a href={`${API}/public/quotes/${quote.id}/pdf`} target="_blank" rel="noopener noreferrer" data-testid="modal-download-pdf">
                    <Button className="mt-4 w-full rounded-xl bg-[#1E88E5] hover:bg-[#1565C0] text-white py-5 font-medium">
                      <Download className="w-4 h-4 mr-2" /> Download branded PDF
                    </Button>
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLeads;
